import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { MagnifyingGlass, Warning } from 'phosphor-react';
import moment from 'moment';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { IContratoSocietario, useSocietario } from '../../../../hooks/useSocietario';
import { ILegalizacao, TipoLegalizacao, useLegalizacao } from '../../../../hooks/useLegalizacao';
import { formatarCnpj } from '../legalizacaoPlaybook';
import { buscaInicial, CONTRATOS_DO_PROCESSO, rotuloDoStatus } from '../contratoDoCard';

interface VincularContratoModalProps {
  isOpen: boolean;
  onClose: () => void;
  legalizacao: ILegalizacao;
  tipo: TipoLegalizacao;
  /** Título do card: a busca inicial na constituição (sem CNPJ ainda). */
  titulo?: string | null;
  contratoAtualId?: number | null;
  isLoading?: boolean;
  // eslint-disable-next-line no-unused-vars
  onConfirm: (contratoId: number) => void;
}

const NOME_DO_TIPO: Record<string, string> = {
  constituicao: 'constituição',
  alteracao: 'alteração',
  transformacao: 'transformação',
};

/**
 * Vincula ao card um contrato que já existe no sistema (gerado no módulo Contratos) ou
 * troca o vinculado. Lista os contratos do tipo do processo, com a busca já preenchida.
 * Contrato ligado a outro processo pode (decisão do user, 30/09), mas pede confirmação.
 */
export const VincularContratoModal: React.FC<VincularContratoModalProps> = ({
  isOpen,
  onClose,
  legalizacao,
  tipo,
  titulo,
  contratoAtualId,
  isLoading,
  onConfirm,
}) => {
  const { getContratos } = useSocietario();
  const { listarLegalizacoes } = useLegalizacao();
  const tipos = CONTRATOS_DO_PROCESSO[tipo] ?? [];

  const [texto, setTexto] = useState('');
  const [busca, setBusca] = useState('');
  const [escolhido, setEscolhido] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const inicial = buscaInicial(legalizacao.cnpj, titulo);
    setTexto(inicial);
    setBusca(inicial);
    setEscolhido(null);
  }, [isOpen, legalizacao.cnpj, titulo]);

  // Busca ao parar de digitar.
  useEffect(() => {
    const t = setTimeout(() => setBusca(texto.trim()), 350);
    return () => clearTimeout(t);
  }, [texto]);

  const { data: contratos = [], isFetching } = useQuery({
    queryKey: ['legalizacao-vincular-contratos', tipos, busca],
    queryFn: async () => {
      // Um pedido por tipo, filtrado no servidor: filtrar aqui depois do corte de 30
      // escondia o contrato certo numa busca ampla.
      const listas = await Promise.all(
        tipos.map(async tipoOperacao => {
          const filtros = {
            search: busca || undefined,
            ordenar: 'atualizados' as const,
            size: 30,
            tipo_operacao: tipoOperacao,
          };
          const res = await getContratos({
            queryKey: ['societario-contratos', filtros],
          } as unknown as Parameters<typeof getContratos>[0]);
          return res.data?.items ?? [];
        }),
      );
      // Cancelado não serve ao processo: o card já nasceria com o aviso de fora do fluxo.
      return ([] as IContratoSocietario[])
        .concat(...listas)
        .filter(c => c.status !== 'cancelado')
        .sort((a, b) => (b.dt_atualizacao ?? '').localeCompare(a.dt_atualizacao ?? ''))
        .slice(0, 30);
    },
    enabled: isOpen && tipos.length > 0,
    placeholderData: keepPreviousData,
  });

  // A que processos cada contrato já está ligado (fora este). Mesma chave e mesmo formato
  // da Visão geral (a resposta inteira): as duas telas dividem o cache.
  const { data: todas } = useQuery({
    queryKey: ['legalizacoes', 'todas'],
    queryFn: () => listarLegalizacoes(),
    enabled: isOpen,
  });
  const ligadoA = useMemo(() => {
    const mapa = new Map<number, string[]>();
    const processos: ILegalizacao[] = Array.isArray(todas?.data) ? todas!.data : [];
    processos.forEach(p => {
      if (!p.contrato_societario_id || p.id === legalizacao.id) return;
      const nomes = mapa.get(p.contrato_societario_id) ?? [];
      nomes.push(p.titulo || `processo #${p.id}`);
      mapa.set(p.contrato_societario_id, nomes);
    });
    return mapa;
  }, [todas, legalizacao.id]);

  const outroProcesso = escolhido ? ligadoA.get(escolhido) : undefined;
  const acaoLabel = contratoAtualId ? 'Trocar para' : 'Vincular';

  const linha = (c: IContratoSocietario) => {
    const atual = c.id === contratoAtualId;
    const selecionado = c.id === escolhido;
    const outros = ligadoA.get(c.id);
    return (
      <Box
        key={c.id}
        as="button"
        type="button"
        textAlign="left"
        w="full"
        px={3}
        py={2.5}
        borderWidth="1px"
        borderRadius="lg"
        borderColor={selecionado ? 'purple.400' : 'gray.200'}
        bg={selecionado ? 'purple.50' : 'white'}
        opacity={atual ? 0.6 : 1}
        cursor={atual ? 'default' : 'pointer'}
        _hover={atual ? undefined : { borderColor: 'purple.300' }}
        onClick={atual ? undefined : () => setEscolhido(c.id)}
        aria-pressed={selecionado}
      >
        <Flex align="center" gap={2}>
          <Text fontSize="13px" fontWeight="600" color="gray.800" noOfLines={1} flex={1}>
            #{c.id} · {c.razao_social || c.razao_social_consolidada || 'Sem razão social'}
          </Text>
          <Badge fontSize="10px" borderRadius="full" px={2} colorScheme="gray">
            {rotuloDoStatus(c.status)}
          </Badge>
        </Flex>
        <Text fontSize="12px" color="gray.600" mt={0.5}>
          {[
            c.cnpj ? `CNPJ ${formatarCnpj(c.cnpj)}` : null,
            NOME_DO_TIPO[c.tipo_operacao] ?? c.tipo_operacao,
            c.dt_atualizacao ? `atualizado ${moment(c.dt_atualizacao).format('DD/MM/YYYY')}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
        {atual && (
          <Text fontSize="11px" color="gray.500" mt={0.5}>
            Vinculado a este card
          </Text>
        )}
        {!atual && outros && (
          <Text fontSize="11px" color="orange.700" mt={0.5}>
            Também vinculado a: {outros.join(', ')}
          </Text>
        )}
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent borderRadius="xl">
        <ModalHeader fontSize="17px" fontWeight="700" pb={1}>
          {contratoAtualId ? 'Trocar contrato' : 'Vincular contrato existente'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="12px" color="gray.600" mb={3}>
            Contratos de {tipos.map(t => NOME_DO_TIPO[t] ?? t).join(' ou ')} já feitos no sistema.
            Busque por empresa, CNPJ, número (#123) ou sócio.
          </Text>
          <InputGroup size="sm" mb={3}>
            <InputLeftElement pointerEvents="none">
              <MagnifyingGlass size={14} color="var(--chakra-colors-gray-400)" />
            </InputLeftElement>
            <Input
              value={texto}
              onChange={e => setTexto(e.target.value)}
              placeholder="Empresa, CNPJ, #número ou sócio"
              borderRadius="md"
              aria-label="Buscar contrato"
            />
          </InputGroup>
          <Stack spacing={2} minH="120px">
            {isFetching && contratos.length === 0 && (
              <Flex justify="center" py={6}>
                <Spinner size="sm" color="gray.400" />
              </Flex>
            )}
            {!isFetching && contratos.length === 0 && (
              <Text fontSize="13px" color="gray.500" textAlign="center" py={6}>
                Nenhum contrato encontrado. Ajuste a busca.
              </Text>
            )}
            {contratos.map(linha)}
          </Stack>
        </ModalBody>
        <ModalFooter flexDirection="column" alignItems="stretch" gap={2}>
          {outroProcesso && (
            <Flex gap={2} align="flex-start" bg="orange.50" borderRadius="md" px={3} py={2}>
              <Box mt={0.5}>
                <Warning size={15} weight="fill" color="var(--chakra-colors-orange-500)" />
              </Box>
              <Text fontSize="12px" color="orange.800">
                Este contrato já está vinculado a {outroProcesso.join(', ')}. Ele continua lá e
                passa a valer também para este card.
              </Text>
            </Flex>
          )}
          <Flex justify="flex-end" gap={3}>
            <Button variant="ghost" borderRadius="full" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="purple"
              borderRadius="full"
              isDisabled={!escolhido}
              isLoading={isLoading}
              onClick={() => escolhido && onConfirm(escolhido)}
            >
              {escolhido
                ? `${acaoLabel} #${escolhido}${outroProcesso ? ' mesmo assim' : ''}`
                : acaoLabel}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
